/**
 * VRTeleportComponent - Drives teleportation using VR axis input.
 */

import * as THREE from 'three';

import { SceneComponent } from '../../components/SceneComponent.js';
import { PhysicsMotionType } from '../../physics/PhysicsTypes.js';
import { EngineClass } from '../../systems/ClassRegistry.js';
import { property } from '../../utils/serialization/decorator.js';
import { XRAxis } from '../VRControllerManager.js';
import { VRPawn } from '../VRPawn.js';

import type { SceneComponentOptions } from '../../components/SceneComponent.js';
import type { XRSqueezeEvent } from '../VRControllerManager.js';
import type { IVRInputHandler } from '../VRPawn.js';

export interface VRTeleportOptions extends SceneComponentOptions {
  axis?: XRAxis;
  startThreshold?: number;
  releaseThreshold?: number;
  handedness?: 'left' | 'right' | 'any';
  maxDistance?: number;
}

@EngineClass('VRTeleportComponent')
export class VRTeleportComponent extends SceneComponent implements IVRInputHandler {


  @property()
  public axis: XRAxis = XRAxis.ThumbstickY;

  @property({ type: 'number', min: -1, max: 1, step: 0.01 })
  public startThreshold: number = -0.7;

  @property({ type: 'number', min: -1, max: 1, step: 0.01 })
  public releaseThreshold: number = -0.2;

  @property()
  public handedness: 'left' | 'right' | 'any' = 'any';

  @property()
  public maxDistance: number = 10;


  private pawn: VRPawn | null = null;
  private aimingHand: 'left' | 'right' | null = null;
  private teleportTargetIndicator: THREE.Mesh | null = null;
  private teleportRayLine: THREE.Line | null = null;
  private teleportTargetPosition: THREE.Vector3 | null = null;
  private pendingCommitTarget: THREE.Vector3 | null = null;
  private frozenHeadLocalXZ: THREE.Vector3 | null = null;

  public override initialize(options?: VRTeleportOptions): void {
    super.initialize(options);
    this.axis = options?.axis ?? this.axis;
    this.startThreshold = options?.startThreshold ?? this.startThreshold;
    this.releaseThreshold = options?.releaseThreshold ?? this.releaseThreshold;
    this.handedness = options?.handedness ?? this.handedness;
    this.maxDistance = options?.maxDistance ?? this.maxDistance;
  }



  public override beginPlay(): void {
    super.beginPlay();
    const actor = this.getActor();
    if (actor instanceof VRPawn) {
      this.pawn = actor;
      this.pawn.addVRInputHandler(this, 10);
    }
  }

  public override endPlay(): void {
    if (this.pawn) {
      this.pawn.removeVRInputHandler(this);
    }
    this.pawn = null;
    this.aimingHand = null;
    this.disposeTeleportVisuals();
    super.endPlay();
  }

  public handleVRAxisChange(inputSource: XRInputSource, axis: XRAxis, value: number): boolean {
    if (!this.pawn) return false;
    if (axis !== this.axis) return false;

    const hand = inputSource.handedness as 'left' | 'right' | 'none';
    if (this.handedness !== 'any' && hand !== this.handedness) return false;
    if (hand === 'none') return false;

    if (value <= this.startThreshold) {
      if (!this.aimingHand) {
        this.aimingHand = hand;
        this.beginTeleportAim();
      }
      if (this.aimingHand === hand) {
        this.updateTeleportAimFrom(inputSource);
        return true;
      }
      return false;
    }

    if (this.aimingHand === hand && value >= this.releaseThreshold) {
      this.commitTeleportIfValid();
      this.aimingHand = null;
      return true;
    }

    return false;
  }

  //======================== Hand Tracking (Pinch) ========================

  public handleSelectStart(event: XRSqueezeEvent): boolean {
    const pawn = this.pawn; if (!pawn) return false;
    const hand = event.inputSource.handedness as 'left' | 'right' | 'none';
    if (hand === 'none') return false;

    // Respect handedness filter
    if (this.handedness !== 'any' && hand !== this.handedness) return false;

    // Only drive hand-tracking pinch (avoid hijacking controller trigger)
    if (!event.inputSource.hand) return false;

    if (!this.aimingHand) {
      this.aimingHand = hand;
      this.beginTeleportAim();
    }
    if (this.aimingHand === hand) {
      this.updateTeleportAimFrom(event.inputSource);
      return true;
    }
    return false;
  }

  public handleSelect(event: XRSqueezeEvent): boolean {
    const hand = event.inputSource.handedness as 'left' | 'right' | 'none';
    if (hand === 'none') return false;
    if (this.aimingHand === hand) {
      this.updateTeleportAimFrom(event.inputSource);
      return true;
    }
    return false;
  }

  public handleSelectEnd(event: XRSqueezeEvent): boolean {
    const hand = event.inputSource.handedness as 'left' | 'right' | 'none';
    if (hand === 'none') return false;
    if (this.aimingHand === hand) {
      this.commitTeleportIfValid();
      this.aimingHand = null;
      return true;
    }
    return false;
  }

  //======================== Teleport Aim & Visuals ========================

  private beginTeleportAim(): void {
    this.createTeleportVisuals();
  }

  private updateTeleportAimFrom(inputSource: XRInputSource): void {
    const hand = inputSource.handedness as 'left' | 'right';
    this.updateTeleportAimForHand(hand);
  }

  private updateTeleportAimForHand(hand: 'left' | 'right'): void {
    const pawn = this.pawn; if (!pawn) return;
    const rayPose = pawn.getTargetRayPose(hand);
    if (!rayPose) { this.teleportTargetPosition = null; this.hideTeleportVisuals(); return; }

    const world = pawn.getWorld();
    const hitResults = world?.getPhysicsEngine()?.performHitTest({
      origin: rayPose.origin,
      direction: rayPose.direction,
      maxDistance: this.maxDistance,
      stopOnFirstHit: false,
      ignoredActors: [pawn]
    });

    if (hitResults && hitResults.length > 0) {
      // Prefer static, nearly-horizontal surfaces (walkable). Fallback to first static.
      const walkable = hitResults
        .filter(h => h.hitComponent?.getMotionType() === PhysicsMotionType.Static)
        .sort((a, b) => Math.abs(b.hitNormal.y) - Math.abs(a.hitNormal.y));
      const best = walkable[0] ?? hitResults.find(h => h.hitComponent?.getMotionType() === PhysicsMotionType.Static);
      if (best) {
        const pos = best.hitLocation.clone();
        // Clamp Y to surface and optionally flatten on slight slopes
        if (Math.abs(best.hitNormal.y) > 0.7) {
          // keep as is; considered walkable
        }
        this.teleportTargetPosition = pos;
        this.updateTeleportVisuals(rayPose.origin, this.teleportTargetPosition);
      } else {
        this.teleportTargetPosition = null;
        this.hideTeleportVisuals();
      }
    } else {
      this.teleportTargetPosition = null;
      this.hideTeleportVisuals();
    }
  }

  private commitTeleportIfValid(): void {
    // Defer commit to next tick to ensure XR frame/reference space availability
    if (this.teleportTargetPosition) {
      this.pendingCommitTarget = this.teleportTargetPosition.clone();
      // Freeze head local XZ at release time for stable offset
      const pawn = this.pawn; if (pawn) {
        const xz = pawn.getCurrentHeadLocalPositionXZ();
        if (xz) pawn.setTeleportHeadLocalXZ(xz);
      }
    } else {
      this.pendingCommitTarget = null;
    }
    this.teleportTargetPosition = null;
    this.hideTeleportVisuals();
  }

  private createTeleportVisuals(): void {
    const pawn = this.pawn; const world = pawn?.getWorld(); if (!world) return;
    if (!this.teleportTargetIndicator) {
      const geometry = new THREE.CylinderGeometry(0.5, 0.5, 0.05, 16);
      const material = new THREE.MeshBasicMaterial({ color: 0x00ff00, transparent: true, opacity: 0.7 });
      this.teleportTargetIndicator = new THREE.Mesh(geometry, material);
      world.scene.add(this.teleportTargetIndicator);
    }
    if (!this.teleportRayLine) {
      const geometry = new THREE.BufferGeometry();
      const material = new THREE.LineBasicMaterial({ color: 0x00ff00, transparent: true, opacity: 0.6 });
      this.teleportRayLine = new THREE.Line(geometry, material);
      world.scene.add(this.teleportRayLine);
    }
  }

  private updateTeleportVisuals(origin: THREE.Vector3, target: THREE.Vector3): void {
    if (this.teleportTargetIndicator) {
      this.teleportTargetIndicator.position.copy(target);
      this.teleportTargetIndicator.visible = true;
    }
    if (this.teleportRayLine) {
      // Draw a straight ray to target for clarity
      const points = [origin, target];
      const geometry = new THREE.BufferGeometry().setFromPoints(points);
      this.teleportRayLine.geometry.dispose();
      this.teleportRayLine.geometry = geometry;
      this.teleportRayLine.visible = true;
    }
  }

  private hideTeleportVisuals(): void {
    if (this.teleportTargetIndicator) this.teleportTargetIndicator.visible = false;
    if (this.teleportRayLine) this.teleportRayLine.visible = false;
  }

  public override tickPostPhysics(deltaTime: number): void {
    super.tickPostPhysics(deltaTime);
    if (this.aimingHand) {
      this.updateTeleportAimForHand(this.aimingHand);
    }
    if (this.pendingCommitTarget && this.pawn) {
      const target = this.pendingCommitTarget.clone();
      this.pendingCommitTarget = null;
      this.pawn.applyTeleportTo(target);
    }
  }

  private disposeTeleportVisuals(): void {
    const pawn = this.pawn; const world = pawn?.getWorld();
    if (this.teleportTargetIndicator) {
      world?.scene.remove(this.teleportTargetIndicator);
      this.teleportTargetIndicator.geometry.dispose();
      (this.teleportTargetIndicator.material as THREE.Material).dispose();
      this.teleportTargetIndicator = null;
    }
    if (this.teleportRayLine) {
      world?.scene.remove(this.teleportRayLine);
      this.teleportRayLine.geometry.dispose();
      (this.teleportRayLine.material as THREE.Material).dispose();
      this.teleportRayLine = null;
    }
  }
}


